/**
 * ErrorBoundary - Catches and displays React errors
 * @module components/starnexus/common/ErrorBoundary
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import SpaceBetween from '@amzn/awsui-components-console/space-between';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Container header={<Header variant="h2">Something went wrong</Header>}>
          <SpaceBetween size="m">
            <Box color="text-status-error">
              <strong>Error:</strong>{' '}
              {this.state.error?.message || 'Unknown error'}
            </Box>
            {this.state.error?.stack && (
              <Box>
                <details>
                  <summary style={{ cursor: 'pointer' }}>Stack trace</summary>
                  <pre
                    style={{
                      fontSize: '12px',
                      overflow: 'auto',
                      backgroundColor: '#f4f4f4',
                      padding: '12px',
                      borderRadius: '4px',
                      marginTop: '8px'
                    }}
                  >
                    {this.state.error.stack}
                  </pre>
                </details>
              </Box>
            )}
            {this.state.errorInfo?.componentStack && (
              <Box>
                <details>
                  <summary style={{ cursor: 'pointer' }}>
                    Component stack
                  </summary>
                  <pre
                    style={{
                      fontSize: '12px',
                      overflow: 'auto',
                      backgroundColor: '#f4f4f4',
                      padding: '12px',
                      borderRadius: '4px',
                      marginTop: '8px'
                    }}
                  >
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              </Box>
            )}
            <Button onClick={this.handleReset}>Try again</Button>
          </SpaceBetween>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
